import { computed, effectScope, onMounted, ref, watch } from 'vue'
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useSubscriptionState } from './useSubscriptionState'
import { pmeBilledMonths } from '@/subscriptionFlows/pmeBilling'

/**
 * État de souscription partagé (singleton) + tarifs : source unique de vérité
 * pour le formulaire (SubscriptionStartCard) et le panneau « Votre tarif » (QuotePanel).
 */

export type PlanPrice = { pricePerMonth: number; pricePerYear: number }

/** Tarifs des formules complémentaire santé (€). */
export const PLAN_PRICING: Record<string, PlanPrice> = {
  minimal:   { pricePerMonth: 5,    pricePerYear: 60 },
  essential: { pricePerMonth: 13.8, pricePerYear: 165.62 },
  confort:   { pricePerMonth: 25.5, pricePerYear: 306 },
  allRisk:   { pricePerMonth: 41,   pricePerYear: 492 },
}
/** Formatage € localisé : 2 décimales seulement s'il y a des centimes.
 *  `alwaysCents` force les 2 décimales — attendu sur un relevé de montants
 *  (« 25,00 € » et non « 25 € »). */
export function formatEur(value: number, locale: string, alwaysCents = false): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: alwaysCents || !Number.isInteger(value) ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value)
}

export type QuoteLine = {
  kind: 'plan' | 'topUp' | 'crossSell' | 'examInsurance'
  value: string
  /** Optional i18n key (in the `bundles` namespace) for a backend-sourced label,
   *  preferred over the front's hard-coded label when present (health journey). */
  labelKey?: string
  perMonth: number
  perYear: number
}

/** État de formulaire partagé entre composants (durée → total dynamique).
 *  Persisté en sessionStorage : survit au F5, oublié à la fermeture de l'onglet. */
// Exported so useSubscriptionFlow can derive compute_price dates from the same
// shared form state (single source of truth for the start date / duration).
export const startDate = ref<Date | null>(null)
export const duration = ref<number | null>(null)
/** Fin de séjour (parcours étranger : plage de dates → durée en mois).
 *  Exporté pour que useSubscriptionFlow dérive la période compute_price depuis
 *  le même état de formulaire partagé. */
export const stayEnd = ref<Date | null>(null)
/** Code ISO du pays de destination (parcours étranger). Exporté pour que
 *  useContractDraft l'envoie en métadonnée de contrat depuis le même état partagé. */
export const destinationCountry = ref<string | null>(null)
/** Code ISO de la nationalité (parcours « j'arrive en France »). Exporté pour que
 *  useContractDraft l'envoie en métadonnée de contrat depuis le même état partagé. */
export const nationality = ref<string | null>(null)

const SS_START = 'smerra:subscription:startDate'
const SS_DURATION = 'smerra:subscription:duration'
const SS_STAY_END = 'smerra:subscription:stayEnd'
const SS_DESTINATION_COUNTRY = 'smerra:subscription:destinationCountry'
const SS_NATIONALITY = 'smerra:subscription:nationality'
let persistenceInstalled = false

/** Durée (en mois entamés) entre deux dates, arrondie au mois supérieur.
 *  Ex. 26/06/2026 → 25/06/2027 = 12 mois ; 26/06 → 25/07 = 1 mois. */
export function monthsBetween(start: Date, end: Date): number {
  const inclusiveEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
  let m = (inclusiveEnd.getFullYear() - start.getFullYear()) * 12 + (inclusiveEnd.getMonth() - start.getMonth())
  if (inclusiveEnd.getDate() < start.getDate()) m -= 1
  const anchor = new Date(start.getFullYear(), start.getMonth() + m, start.getDate())
  if (anchor < inclusiveEnd) m += 1
  return Math.max(1, m)
}

/** Clé sessionStorage propre au produit : la date saisie sur un parcours ne doit
 *  pas « baver » sur un autre (le ref est un singleton partagé entre composants). */
function keyFor(base: string, product: string | undefined): string {
  return product ? `${base}:${product}` : base
}

function readDate(key: string): Date | null {
  const raw = window.sessionStorage.getItem(key)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'string') {
      const d = new Date(parsed)
      if (!isNaN(d.getTime())) return d
    }
  } catch { /* ignore */ }
  return null
}

/** (Re)charge date + durée pour le produit courant. Date vierge si rien de
 *  mémorisé pour CE produit ; durée par défaut = 12 mois (la plus longue). */
function loadFor(product: string | undefined): void {
  if (typeof window === 'undefined') return
  startDate.value = readDate(keyFor(SS_START, product))
  stayEnd.value = readDate(keyFor(SS_STAY_END, product))
  destinationCountry.value = window.sessionStorage.getItem(keyFor(SS_DESTINATION_COUNTRY, product)) || null
  nationality.value = window.sessionStorage.getItem(keyFor(SS_NATIONALITY, product)) || null
  let nextDuration: number | null = null
  const du = window.sessionStorage.getItem(keyFor(SS_DURATION, product))
  if (du) {
    try {
      const parsed = JSON.parse(du)
      if (typeof parsed === 'number') nextDuration = parsed
    } catch { /* ignore */ }
  }
  // Pré-sélection de 12 mois pour la santé, la responsabilité civile, la
  // surcomplémentaire, la mutuelle AURA et Solidarité Exam (couverture annuelle,
  // pas de sélecteur de durée) ; sinon pas de durée par défaut (étranger plage de
  // dates ; France paliers dédiés).
  duration.value =
    nextDuration ??
    (product === 'healthExpenses' ||
    product === 'civil' ||
    product === 'healthTopUp' ||
    product === 'regionalAura' ||
    product === 'solidariteExam'
      ? 12
      : null)
}

/** Keeps the subscriber's choices (start date, duration, destination…) saved
 *  for the whole visit, whatever screen is displayed.
 *
 *  The saving must not depend on the component that started it. For a logged-in
 *  member, the product page first renders its public version, then switches to
 *  the member-area version as soon as the session is known. Tied to the first
 *  version, the saving stopped at that switch: after the round trip to Stripe
 *  the form came back with the default date and duration, and the member could
 *  pay for a period they never chose. This scope lives as long as the page. */
const persistenceScope = effectScope(true)

/** Restores the choices the subscriber already made for this product, then
 *  saves every new choice as it happens. When the subscriber moves to another
 *  product, that product's own choices are restored instead (or a blank form).
 *  Runs once per page, however many components use the subscription form.
 *  The current product is read from the route, which lasts as long as the page,
 *  so it keeps being followed after the calling component is gone. */
function installPersistence(route: RouteLocationNormalizedLoaded): void {
  if (persistenceInstalled || typeof window === 'undefined') return
  persistenceInstalled = true

  persistenceScope.run(() => {
    const product = computed<string | undefined>(() => route.meta.productKey)

    loadFor(product.value)

    watch(startDate, (v) => {
      const key = keyFor(SS_START, product.value)
      if (v) window.sessionStorage.setItem(key, JSON.stringify(v.toISOString()))
      else window.sessionStorage.removeItem(key)
    })
    watch(duration, (v) => {
      const key = keyFor(SS_DURATION, product.value)
      if (v !== null) window.sessionStorage.setItem(key, JSON.stringify(v))
      else window.sessionStorage.removeItem(key)
    })
    watch(stayEnd, (v) => {
      const key = keyFor(SS_STAY_END, product.value)
      if (v) window.sessionStorage.setItem(key, JSON.stringify(v.toISOString()))
      else window.sessionStorage.removeItem(key)
    })
    watch(destinationCountry, (v) => {
      const key = keyFor(SS_DESTINATION_COUNTRY, product.value)
      if (v) window.sessionStorage.setItem(key, v)
      else window.sessionStorage.removeItem(key)
    })
    watch(nationality, (v) => {
      const key = keyFor(SS_NATIONALITY, product.value)
      if (v) window.sessionStorage.setItem(key, v)
      else window.sessionStorage.removeItem(key)
    })

    // Switching product: restore that product's own choices, never the previous one's.
    watch(product, (now, prev) => {
      if (now !== prev) loadFor(now)
    })
  })
}

export function useSubscription() {
  const { answers, locale, product } = useSubscriptionState()
  const route = useRoute()
  onMounted(() => installPersistence(route))

  /** Responsabilité civile : le devis ne montre que l'offre choisie (prix annuel). */
  const isCivil = computed(() => product.value === 'civil')

  /** Un produit a été choisi à la sortie de l'arbre (formule renseignée). */
  const hasChosenProduct = computed(() => Boolean(answers.value.plan))

  /** Lignes du devis pour les produits estimés côté front (repli hérité) ; tous
   *  les parcours actuels sont branchés au back-end et passent par
   *  useSubscriptionFlow. */
  const lines = computed<QuoteLine[]>(() => {
    const out: QuoteLine[] = []
    const plan = answers.value.plan
    if (plan && PLAN_PRICING[plan]) {
      out.push({
        kind: 'plan',
        value: plan,
        perMonth: PLAN_PRICING[plan].pricePerMonth,
        perYear: PLAN_PRICING[plan].pricePerYear,
      })
    }
    return out
  })

  const isHealthExpenses = computed(() => product.value === 'healthExpenses')
  const isAbroad = computed(() => product.value === 'abroad')
  const isArrivingFrance = computed(() => product.value === 'arrivingFrance')
  const isHealthTopUp = computed(() => product.value === 'healthTopUp')
  const isRegionalAura = computed(() => product.value === 'regionalAura')
  const isSolidariteExam = computed(() => product.value === 'solidariteExam')

  const monthlyTotal = computed(() => lines.value.reduce((s, l) => s + l.perMonth, 0))
  const yearlyTotal = computed(() => lines.value.reduce((s, l) => s + l.perYear, 0))

  /** Durée du séjour en mois (parcours étranger : plage de dates choisie). */
  const stayMonths = computed(() =>
    startDate.value && stayEnd.value ? monthsBetween(startDate.value, stayEnd.value) : null,
  )

  /** Durée du séjour en jours (bornes incluses) — pour les courts séjours. */
  const stayDays = computed(() => {
    if (!startDate.value || !stayEnd.value) return null
    const ms = stayEnd.value.getTime() - startDate.value.getTime()
    return Math.round(ms / 86400000) + 1
  })

  /** Mois facturés du séjour (parcours étranger / bundles PME) : mois pleins +
   *  prorata des jours restants (≤ 15 → demi-mois, > 15 → mois plein). Reflète le
   *  calcul back-end `pme_price` ; peut être fractionnaire (ex. 2,5). */
  const billedMonths = computed(() =>
    startDate.value && stayEnd.value ? pmeBilledMonths(startDate.value, stayEnd.value) : null,
  )

  /** Total souscription (estimation front, repli hérité) : tarif annuel au prorata
   *  de la durée souscrite. Les parcours branchés au back-end passent par
   *  compute_price (useSubscriptionFlow). */
  const subscriptionTotal = computed(() => {
    const months = duration.value
    if (!months) return null
    return Math.round(((yearlyTotal.value * months) / 12) * 100) / 100
  })

  return {
    locale,
    isCivil,
    isHealthExpenses,
    isAbroad,
    isArrivingFrance,
    isHealthTopUp,
    isRegionalAura,
    isSolidariteExam,
    startDate,
    stayEnd,
    destinationCountry,
    nationality,
    duration,
    stayMonths,
    stayDays,
    billedMonths,
    hasChosenProduct,
    lines,
    monthlyTotal,
    yearlyTotal,
    subscriptionTotal,
  }
}
